import { FileTypes } from '@aims/shared/files/constants';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import { Button, Tooltip, Upload, notification } from 'antd';
import ImgCrop from 'antd-img-crop';
import React, { useCallback, useRef, useState } from 'react';
import { getAttachmentUploadUrlForAdminMutation } from '../../../../../components/AnyAttachments/constants';
import {
  doFileUpload,
  generateUuid,
  toBase64,
} from '../../../../../shared/utils';

export const saveHolisticProjectPhotoMutation = gql`
  mutation SaveHolisticProjectPhoto($photo: CreateHolisticPhotoInput!) {
    saveHolisticProjectPhoto(photo: $photo)
  }
`;

function UploadPhotoButton({
  holisticProject,
  refetch,
  fileType = FileTypes.H_PHOTO.key,
  title = 'Upload Photo',
  aspect = 1,
}) {
  const _fileList = useRef([]);
  const [fileList, _setFileList] = useState(_fileList.current);
  const setFileList = useCallback((value) => {
    _fileList.current = value;
    _setFileList(value);
  }, []);
  const [uploadLoading, setUploadLoading] = useState(false);
  const _uploadProgress = useRef({});
  const [uploadProgress, _setUploadProgress] = useState(
    _uploadProgress.current,
  );
  const setUploadProgress = useCallback((value) => {
    _uploadProgress.current = value;
    _setUploadProgress(value);
  }, []);
  const [error, setError] = useState(null);
  const [getAttachmentUploadUrlForAdmin] = useMutation(
    getAttachmentUploadUrlForAdminMutation,
  );
  const [saveHolisticProjectPhoto] = useMutation(
    saveHolisticProjectPhotoMutation,
  );

  const doUpload = useCallback(
    async (obj) => {
      try {
        setError(null);
        setUploadLoading(true);
        const photoId = generateUuid();
        const s3Key = `holistic-project/${holisticProject._id}/photo/${photoId}`;

        let base64Url;
        if (obj.file.type.startsWith('image')) {
          base64Url = await toBase64(obj.file);
        }
        setFileList([
          ..._fileList.current,
          {
            _id: photoId,
            s3Key,
            size: obj.file.size,
            filename: obj.file.name,
            contentType: obj.file.type,
            base64Url,
          },
        ]);

        let response;
        response = await getAttachmentUploadUrlForAdmin({
          variables: {
            attachment: {
              _id: photoId,
              s3Key,
            },
          },
        });
        if (
          !response ||
          !response.data ||
          !response.data.getAttachmentUploadUrlForAdmin
        ) {
          throw new Error('Bad upload URL');
        }

        setUploadProgress(0);
        await doFileUpload({
          url: response.data.getAttachmentUploadUrlForAdmin.url,
          fields: response.data.getAttachmentUploadUrlForAdmin.fields,
          file: obj.file,
          onProgress: (event) => {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          },
        });
        setTimeout(() => {
          setUploadProgress(undefined);
        }, 1000);
        await saveHolisticProjectPhoto({
          variables: {
            photo: {
              _id: photoId,
              filename: obj.file.name,
              s3Key,
              attachedTo: holisticProject._id,
              type: fileType,
            },
          },
        });

        refetch?.();
        notification.success({
          message: 'Saved',
          description: 'Image uploaded successfully',
        });
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: 'There was an error uploading your image',
        });
      }
      setUploadLoading(false);
    },
    [
      saveHolisticProjectPhoto,
      getAttachmentUploadUrlForAdmin,
      holisticProject,
      setUploadProgress,
      setFileList,
      refetch,
      fileType,
    ],
  );
  return (
    <ImgCrop aspect={aspect}>
      <Upload
        className="profile-pic-upload"
        showUploadList={false}
        listType="picture"
        customRequest={doUpload}
      >
        {error && (
          <Tooltip title={error}>
            <Button danger icon={<ExclamationCircleOutlined />}>
              Failed
            </Button>
          </Tooltip>
        )}
        {!error && (
          <Button
            disabled={!holisticProject}
            loading={uploadLoading}
            icon={<PlusOutlined />}
          >
            {uploadLoading
              ? uploadProgress !== undefined
                ? `Uploading ... ${uploadProgress} %`
                : 'Saving ...'
              : title}
          </Button>
        )}
      </Upload>
    </ImgCrop>
  );
}

export default UploadPhotoButton;
